import { Box, styled, Typography, useTheme } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  SAIComparisonTable,
  SAIDIalogContainer,
  SButton,
  SText,
  useAITableStyles
} from '@setvi/shared/components';
import { AIContentBox } from '@setvi/shared/components/ai/dialog-container/styled';
import { AIGradientIcon, AIIcon } from '@setvi/shared/components/sicons';
import { useDialog } from '@setvi/shared/providers';
import { Product } from '@setvi/shared/interfaces';

import { compareProductsMutation } from 'Services';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useProductMetadata } from 'pages/products/hooks/use-product-metadata';
import { aiCompareMessages } from 'pages/products/components/ai/loader/messages';

import { RecommendationPurposeId, RecommendationRequest } from '../recommend';
import { DialogLoader, Error } from '../components';
import { Explanation } from './explanation';

const Title = styled(Typography)({
  fontSize: 20,
  fontWeight: 'bold',
  textDecoration: 'underline'
});

const StyledButton = styled(SButton)(({ theme }) => ({
  borderRadius: 120,
  padding: theme.spacing(2, 3)
}));

export const FinalRecommendation = styled(Box)(({ theme: { spacing } }) => ({
  backgroundColor: '#ededed',
  borderRadius: 6,
  padding: spacing(2)
}));

interface CompareDialogContentProps {
  products: Product[];
}

export const CompareDialogId = 'compare-dialog';

export const CompareDialogContent = ({
  products
}: CompareDialogContentProps) => {
  const { palette } = useTheme();
  const { user } = useAppContext();
  const { openDialog, closeDialog } = useDialog();

  const { isLoading: isMetadataLoading, handleExternalMetadata } =
    useProductMetadata({});

  const [meta, setMeta] = useState([]);
  const [loading, setLoading] = useState(false);

  const isInvalid = useMemo(
    () =>
      products.some(
        p =>
          !p?.vendor?.name ||
          (user?.IsManufacturer ? !p?.sku : !p?.manufacturerSku)
      ),
    [products, user]
  );

  const tableStyle = useAITableStyles();
  const { data, isFetching: isCompareLoading } = useQuery({
    ...compareProductsMutation({
      products: meta
    }),
    enabled: !!meta?.length
  });

  const handleMetadata = async (prods: Product[]) => {
    setLoading(true);

    const results = await Promise.all(
      prods.map(async p => {
        const metadata = await handleExternalMetadata(p);
        if (!metadata) return null;
        return {
          _id: p?._id,
          metadata
        };
      })
    );

    setMeta(results.filter(Boolean)); // Remove nulls
    setLoading(false);
  };

  useEffect(() => {
    if (products?.length && !isInvalid) {
      handleMetadata(products);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const isLoading = useMemo(
    () => isMetadataLoading || isCompareLoading || loading,
    [isMetadataLoading, isCompareLoading, loading]
  );

  const getDialogDescription = () => {
    const names = products.map(product => product?.name);
    return `For Products: ${names.join(' vs. ')}`;
  };

  return (
    <SAIDIalogContainer
      headerDivider={false}
      title="AI Comparison Analysis"
      description={getDialogDescription()}
      button={
        <StyledButton
          variant="outlined"
          startIcon={
            isLoading ? (
              <AIIcon fill={palette.text.disabled} />
            ) : (
              <AIGradientIcon />
            )
          }
          onClick={() => {
            closeDialog();
            openDialog(<RecommendationRequest products={products} />, {
              id: RecommendationPurposeId
            });
          }}
          tooltipTitle={
            isLoading
              ? 'Please wait for compare analysis to complete'
              : 'AI can perform a recommendation analysis based on a use case, application or segment'
          }
          disabled={isLoading}>
          Help me Recommend
        </StyledButton>
      }>
      <AIContentBox>
        {isLoading ? (
          <DialogLoader messages={aiCompareMessages} />
        ) : (
          <>
            {data?.error || !data || isInvalid ? (
              <Error
                value={
                  data?.error || 'Not Enough Information to Compare Products'
                }
              />
            ) : (
              <>
                <Title style={{ marginBottom: 4 }}>When to Choose</Title>
                <table className={tableStyle.table}>
                  <tr>
                    {['Ideal for', 'Option'].map(i => (
                      <th key={i}>{i}</th>
                    ))}
                  </tr>
                  {data.whenToChooseProduct.map((i: any) => (
                    <tr key={i.name}>
                      <td>{i.ideal}</td>
                      <td>{i.option}</td>
                    </tr>
                  ))}
                </table>
                <Box my={3}>
                  <Title style={{ marginBottom: 4 }}>AI Comparison</Title>
                  <SAIComparisonTable
                    features={data.table.map((i: any) => {
                      const { feature, ...rest } = i;
                      return {
                        name: feature,
                        values: Object.keys(rest).map(
                          j => i[j as keyof typeof i]
                        )
                      };
                    })}
                    products={products.map(i => i.name)}
                  />
                </Box>
                {data.scenarioBasedRecommendations && (
                  <>
                    <Title>Scenario Based Recommendations</Title>
                    {data.scenarioBasedRecommendations.map(
                      ({ title, choose, why }: any, index: number) => (
                        <Box key={title} my={2} ml={4}>
                          <Box display="flex" alignItems="center" gridGap="4px">
                            <Typography>{index + 1}.</Typography>
                            <SText
                              style={{ textDecoration: 'underline' }}
                              title={title || 'Title Not Provided'}
                              weight="medium"
                            />
                          </Box>
                          <Box ml={4}>
                            <Explanation
                              key={choose}
                              prefix="Choose"
                              value={choose}
                            />
                            <Explanation
                              key={choose}
                              prefix="Why"
                              value={why}
                            />
                          </Box>
                        </Box>
                      )
                    )}
                  </>
                )}
                {data.finalRecommendation && (
                  <FinalRecommendation>
                    <Title>Final Recommendation</Title>
                    <Box
                      ml={4}
                      mt={2}
                      style={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        lineHeight: 1.5
                      }}
                      dangerouslySetInnerHTML={{
                        __html: data.finalRecommendation
                      }}
                    />
                  </FinalRecommendation>
                )}
              </>
            )}
          </>
        )}
      </AIContentBox>
    </SAIDIalogContainer>
  );
};
